import React from "react";
import { confirmDialog } from "primereact/confirmdialog";
import {
  getGroupedBarColorProviders,
  barColorProviders,
  elderImpulseProvider,
  rsiColorProvider,
  stochasticColorProvider,
  macdColorProvider,
} from "../../Registry/BarColorProviders/barColorRegistry";
import indicatorRegistry from "../../Registry/Indicators/IndicatorRegistry";
import { useChartsStore } from "../../store/chartStore";

// Chart types that support bar coloring
export const COLORABLE_CHART_TYPES = ["candle", "hollowcandle", "colorbar"];

// Resolve a provider entry (with availability info for the given indicators)
export const findBarColorProviderOption = (providerKey, activeIndicators) => {
  for (const group of getGroupedBarColorProviders(activeIndicators || [])) {
    const option = group.items.find((item) => item.key === providerKey);
    if (option) return option;
  }
  return null;
};

// Display entry for a provider key (used by valueTemplates / active checkmarks)
export const getBarColorProviderEntry = (providerKey) => {
  let currentProvider = barColorProviders[providerKey];
  if (!currentProvider && providerKey === "indicator:elderImpulse") {
    currentProvider = elderImpulseProvider;
  }
  if (!currentProvider && providerKey === "indicator:rsi") {
    currentProvider = rsiColorProvider;
  }
  if (!currentProvider && providerKey === "indicator:stochastic") {
    currentProvider = stochasticColorProvider;
  }
  if (!currentProvider && providerKey === "indicator:macd") {
    currentProvider = macdColorProvider;
  }
  return currentProvider || null;
};

// Find color-only indicator instances present on the chart
const findColorOnlyIndicators = (chart) => {
  if (!chart?.indicators) return [];

  const colorOnlyIndicators = [];

  // Search through all indicator registries to find color-only indicators
  Object.values(indicatorRegistry).forEach((registry) => {
    Object.entries(registry).forEach(([, config]) => {
      if (config.colorOnly === true) {
        // Find instances of this indicator in the chart
        const instances = chart.indicators.filter(
          (ind) => ind.type === config.type,
        );
        colorOnlyIndicators.push(...instances);
      }
    });
  });

  return colorOnlyIndicators;
};

const removeColorOnlyIndicators = (chartId, chart) => {
  const { removeIndicatorFromChart } = useChartsStore.getState();
  findColorOnlyIndicators(chart).forEach((indicator) => {
    removeIndicatorFromChart(chartId, indicator.id);
  });
};

/**
 * Apply a bar-color provider to a chart — the FULL navbar flow, extracted
 * verbatim from BarColorDropdown.onChange so the chart context menu reuses the
 * exact same behavior (missing-indicator confirm + auto-add, incompatible
 * chart-type confirm, color-only indicator cleanup).
 *
 * @param {string} chartId     target chart id
 * @param {Object} chart       chart object from the store (chartType, indicators)
 * @param {string} newProvider provider key (e.g. "traditional", "indicator:rsi")
 * @param {Function} [onApplied] called after the provider is applied directly
 *                               (not on the async confirm branches)
 */
export const applyBarColorProvider = (
  chartId,
  chart,
  newProvider,
  onApplied,
) => {
  const { updateChartConfig, addIndicatorToChart } = useChartsStore.getState();
  const activeIndicators = chart?.indicators || [];
  const chartType = chart?.chartType || "candle";

  // Find the full provider option with availability info
  const providerOption = findBarColorProviderOption(
    newProvider,
    activeIndicators,
  );

  // Check if provider requires indicators that aren't present
  if (providerOption && providerOption.isAvailable === false) {
    const requiredIndicators = providerOption.requiresIndicators || [];

    // Check which indicators are actually missing
    const missingIndicators = requiredIndicators.filter((indicatorType) => {
      return !activeIndicators.some((ind) => ind.key === indicatorType);
    });

    // If all required indicators are already present, just apply coloring
    if (missingIndicators.length === 0) {
      console.log(
        "[BarColorSelection] All required indicators present, applying coloring",
      );
      updateChartConfig(chartId, { barColorProvider: newProvider });
      return;
    }

    // Get names of missing indicators only
    const indicatorNames = missingIndicators
      .map((indicatorKey) => {
        // Find display name from indicator registry by key
        for (const category of Object.values(indicatorRegistry)) {
          const config = category.indicators?.[indicatorKey];
          if (config) {
            return config.displayName || indicatorKey;
          }
        }
        return indicatorKey;
      })
      .join(", ");

    confirmDialog({
      message: `Esta coloração requer o(s) indicador(es): ${indicatorNames}. Deseja adicionar automaticamente?`,
      header: "Indicador Necessário",
      icon: "pi pi-info-circle",
      acceptLabel: "Sim, adicionar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-success",
      rejectClassName: "p-button-secondary",
      accept: () => {
        // Auto-add only missing indicators
        missingIndicators.forEach((indicatorType) => {
          // Find indicator config from registry by matching the key
          let found = false;
          for (const [categoryKey, category] of Object.entries(
            indicatorRegistry,
          )) {
            for (const [indicatorKey, config] of Object.entries(
              category.indicators || {},
            )) {
              // Match by key (e.g., 'rsi')
              if (indicatorKey === indicatorType) {
                console.log(
                  `[BarColorSelection] Adding indicator ${indicatorType}`,
                );

                // Build default config from defaultOptions
                const defaultConfig = {};
                if (config.defaultOptions) {
                  config.defaultOptions.forEach((option) => {
                    if (option.name && option.defaultValue !== undefined) {
                      defaultConfig[option.name] = option.defaultValue;
                    }
                  });
                }

                // Build appearance config from defaultOptionsForAppearance
                const defaultAppearanceConfig = {};
                if (config.defaultOptionsForAppearance) {
                  config.defaultOptionsForAppearance.forEach((option) => {
                    if (option.name && option.defaultValue !== undefined) {
                      defaultAppearanceConfig[option.name] =
                        option.defaultValue;
                    }
                  });
                }

                // Create indicator instance like RegistryIndicatorsDropdown does
                const id = `${indicatorKey}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const newIndicator = {
                  ...config,
                  id,
                  config: defaultConfig,
                  appearanceConfig: defaultAppearanceConfig,
                  categoryKey,
                  categoryDisplayName: category.displayName,
                };

                addIndicatorToChart(chartId, newIndicator);
                found = true;
                break;
              }
            }
            if (found) break;
          }
        });

        // Then apply bar color provider
        updateChartConfig(chartId, { barColorProvider: newProvider });
      },
    });
    return;
  }

  // If selecting a non-traditional provider and chart type doesn't support coloring
  if (
    newProvider !== "traditional" &&
    !COLORABLE_CHART_TYPES.includes(chartType)
  ) {
    confirmDialog({
      message:
        "O tipo de gráfico atual não suporta coloração personalizada. Deseja mudar para Candles ou Barras Coloridas?",
      header: "Tipo de Gráfico Incompatível",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Candles",
      rejectLabel: "Barras Coloridas",
      acceptClassName: "p-button-success",
      rejectClassName: "p-button-info",
      footer: (
        <>
          <button
            type="button"
            className="p-button p-component p-button-text p-button-secondary"
            onClick={() => {
              // Abort - keep current state
              document
                .querySelector(".p-confirm-dialog .p-dialog-header-close")
                .click();
            }}
            data-evo-id="navbar-bar-color-dialog-cancel"
          >
            <span className="p-button-label">Cancelar</span>
          </button>
          <button
            type="button"
            className="p-button p-component p-button-info"
            onClick={() => {
              // Remove color-only indicators before switching
              removeColorOnlyIndicators(chartId, chart);
              // Change to colorbar and apply bar color provider
              updateChartConfig(chartId, {
                chartType: "colorbar",
                barColorProvider: newProvider,
              });
              document
                .querySelector(".p-confirm-dialog .p-dialog-header-close")
                .click();
            }}
            data-evo-id="navbar-bar-color-dialog-bar-color"
          >
            <span className="p-button-label">Barras Coloridas</span>
          </button>
          <button
            type="button"
            className="p-button p-component p-button-success"
            onClick={() => {
              // Remove color-only indicators before switching
              removeColorOnlyIndicators(chartId, chart);
              // Change to candle and apply bar color provider
              updateChartConfig(chartId, {
                chartType: "candle",
                barColorProvider: newProvider,
              });
              document
                .querySelector(".p-confirm-dialog .p-dialog-header-close")
                .click();
            }}
            data-evo-id="navbar-bar-color-dialog-candle"
          >
            <span className="p-button-label">Candles</span>
          </button>
        </>
      ),
    });
    return;
  }

  // Remove color-only indicators when switching to any strategy (including traditional)
  removeColorOnlyIndicators(chartId, chart);

  // Chart type supports coloring or selecting traditional provider
  updateChartConfig(chartId, { barColorProvider: newProvider });

  if (typeof onApplied === "function") {
    onApplied();
  }
};
